import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { taskScheduleService } from "../services/task-schedule.service";
import type {
	CreateTaskScheduleRequest,
	ErrorResponse,
	ListTaskSchedulesParams,
	UpdateTaskScheduleRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const taskScheduleKeys = {
	all: ["task-schedule"] as const,
	lists: () => [...taskScheduleKeys.all, "list"] as const,
	list: (params?: ListTaskSchedulesParams) => [...taskScheduleKeys.lists(), params] as const,
	details: () => [...taskScheduleKeys.all, "detail"] as const,
	detail: (id: string) => [...taskScheduleKeys.details(), id] as const,
};

// ─── useTaskSchedules (list) ─────────────────────────────────────────
export function useTaskSchedules(params?: ListTaskSchedulesParams) {
	return useQuery({
		queryKey: taskScheduleKeys.list(params),
		queryFn: () => taskScheduleService.list(params),
	});
}

// ─── useCreateTaskSchedule ───────────────────────────────────────────
export function useCreateTaskSchedule() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateTaskScheduleRequest) => taskScheduleService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskScheduleKeys.lists() });
			message.success("Takrorlanish jadvali yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg =
				error.response?.data?.error?.message || "Takrorlanish jadvalini yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateTaskSchedule ───────────────────────────────────────────
export function useUpdateTaskSchedule() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTaskScheduleRequest }) =>
			taskScheduleService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskScheduleKeys.lists() });
			queryClient.invalidateQueries({ queryKey: taskScheduleKeys.detail(variables.id) });
			message.success("Takrorlanish jadvali yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg =
				error.response?.data?.error?.message || "Takrorlanish jadvalini yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteTaskSchedule ───────────────────────────────────────────
export function useDeleteTaskSchedule() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => taskScheduleService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskScheduleKeys.lists() });
			message.success("Takrorlanish jadvali o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg =
				error.response?.data?.error?.message || "Takrorlanish jadvalini o'chirishda xatolik";
			message.error(msg);
		},
	});
}
